import { Tooltip } from 'antd';
import { ChevronDown, ChevronRight } from 'lucide-react';
import React, { useContext } from 'react';
import { AppContext } from 'src/contexts/app.context';
import { generateIcon } from 'src/utils/iconRegistry';
import './ModernSidebar.scss';
import { useModernSidebar, type ModernSidebarProps, type SidebarItem } from './useModernSidebar';

const ModernSidebar: React.FC<ModernSidebarProps> = ({ className = '', isMobileOpen = false }) => {
  const { isSidebarCollapsed } = useContext(AppContext);
  const { sidebarMenuItems, handleItemClick, rootResource } = useModernSidebar();

  // Render icon
  const renderIcon = (icon: string | React.ReactNode | undefined, color?: string) => {
    if (!icon) return null;

    if (typeof icon === 'string') {
      return generateIcon(icon, 18, color ?? '#fff');
    }

    return icon;
  };

  // Render sidebar item recursively
  const renderSidebarItem = (item: SidebarItem, level: number = 0): React.ReactNode => {
    const hasChildren = item.children && item.children.length > 0;
    const isCollapsed = item.isCollapsed;
    const hasNoPermission = item.hasPermission === false;

    return (
      <div key={item.id} className='sidebar-item-container'>
        <Tooltip
          title={isSidebarCollapsed ? item.name : ''}
          placement='right'
          overlayClassName='sidebar-tooltip'
          mouseEnterDelay={0.5}
        >
          <div
            className={`sidebar-item ${item.isActive ? 'active' : ''} ${hasChildren ? 'has-children' : ''} ${
              hasNoPermission ? 'no-permission' : ''
            } level-${level}`}
            onClick={() => handleItemClick(item)}
            style={{ paddingLeft: isSidebarCollapsed ? '4px' : `${8 + level * 20}px`, position: 'relative' }}
          >
            <div className='sidebar-item-content'>
              <span className='sidebar-icon'>{renderIcon(item.icon)}</span>
              {!isSidebarCollapsed && <span className='sidebar-label'>{item.name}</span>}
            </div>
            {hasNoPermission && !isSidebarCollapsed && (
              <span className='lock-icon-line'>{generateIcon('Lock', 12, '#ff4d4f')}</span>
            )}
            {hasChildren && !isSidebarCollapsed && (
              <span className='collapse-indicator'>
                {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
              </span>
            )}
          </div>
        </Tooltip>
        {/* Render children nếu không collapsed và sidebar không collapsed */}
        {hasChildren && !isCollapsed && !isSidebarCollapsed && (
          <div className='sidebar-children'>
            {item.children!.map((child: SidebarItem) => renderSidebarItem(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      className={`modern-sidebar ${isSidebarCollapsed ? 'collapsed' : ''} ${
        isMobileOpen ? 'mobile-open' : ''
      } ${className}`}
    >
      {/* Sidebar Header - Hiển thị Root Resource */}
      {rootResource && (
        <div className='sidebar-header'>
          <div className='sidebar-header-content'>
            <span className='sidebar-header-icon'>{renderIcon(rootResource.icon, '#fff')}</span>
            {!isSidebarCollapsed && <span className='sidebar-header-title'>{rootResource.name}</span>}
          </div>
        </div>
      )}

      {/* Sidebar Content */}
      <div className='sidebar-content'>{sidebarMenuItems.map((item: SidebarItem) => renderSidebarItem(item))}</div>
    </div>
  );
};

export default ModernSidebar;
